import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'

import { cn } from '../../lib/cn'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE =
  'ring-focus inline-flex items-center justify-center gap-2 rounded-sm font-semibold ' +
  'whitespace-nowrap select-none transition-colors ' +
  'disabled:cursor-not-allowed disabled:opacity-45'

const VARIANTS: Record<ButtonVariant, string> = {
  // Brass carries the primary action. Its ink is chrome, never white — see DESIGN_SYSTEM.
  primary: 'bg-brass text-chrome-900 hover:bg-brass-deep hover:text-chrome-ink active:bg-brass-deep',
  // border-hair measures ~1.3:1 here — fails WCAG 1.4.11's 3:1 non-text minimum as this button's
  // only at-rest boundary; border-ink-faint clears it (see Input.tsx's controlSurface, same fix).
  secondary: 'bg-surface text-ink border border-ink-faint hover:bg-sunken active:bg-sunken',
  ghost: 'bg-transparent text-ink-mute hover:bg-sunken hover:text-ink active:bg-sunken',
  danger: 'bg-down text-surface hover:brightness-95 active:brightness-90',
}

/**
 * `sm` and `md` carry the type roles rather than their sizes, so a change to `--t-small-size` or
 * `--t-body-size` reaches the buttons. The role classes add a line height, which a fixed-height
 * `inline-flex items-center` box does not show, and no weight, so `font-semibold` in BASE still
 * decides the weight.
 *
 * `lg` is the exception: a 44 px control wants a step between `--t-body` (14 px) and `--t-h3`
 * (16 px) and the scale has none, so 0.9375rem stays a literal here, in `Input` and in the two
 * other large controls. It should become a token — see the build report.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-[30px] px-3 t-small',
  md: 'h-9 px-4 t-body',
  lg: 'h-11 px-5 text-[0.9375rem]',
}

export interface ButtonStyleOptions {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  className?: string
}

/** Shared so a router `Link` can look exactly like a button without duplicating classes. */
export const buttonClasses = ({
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  className,
}: ButtonStyleOptions = {}): string =>
  cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className)

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>,
    ButtonStyleOptions {
  /** Swaps the label for a spinner and blocks the click. The label still reserves its width. */
  loading?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  children?: ReactNode
}

/**
 * The product's action control. Its label names the outcome — `Schedule post`, not `Submit` —
 * and the confirmation keeps that word.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    fullWidth,
    className,
    loading = false,
    iconLeft,
    iconRight,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={buttonClasses({ variant, size, fullWidth, className })}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 'md' : 'sm'} />
      ) : iconLeft ? (
        <span className="shrink-0" aria-hidden="true">
          {iconLeft}
        </span>
      ) : null}
      {children}
      {!loading && iconRight ? (
        <span className="shrink-0" aria-hidden="true">
          {iconRight}
        </span>
      ) : null}
    </button>
  )
})

export interface ButtonLinkProps extends LinkProps, ButtonStyleOptions {
  iconLeft?: ReactNode
  iconRight?: ReactNode
}

/** A route link wearing button clothes. Use for navigation, never for a mutation. */
export const ButtonLink = ({
  variant = 'secondary',
  size = 'md',
  fullWidth,
  className,
  iconLeft,
  iconRight,
  children,
  ...rest
}: ButtonLinkProps) => (
  <Link className={buttonClasses({ variant, size, fullWidth, className })} {...rest}>
    {iconLeft ? (
      <span className="shrink-0" aria-hidden="true">
        {iconLeft}
      </span>
    ) : null}
    {children}
    {iconRight ? (
      <span className="shrink-0" aria-hidden="true">
        {iconRight}
      </span>
    ) : null}
  </Link>
)
